/**
 * Primitivi condivisi del dominio "rilevazione" (Produzione, Giacenza, Smaltimento).
 * Solo styling — nessuna logica. Tutti i colori derivano dai token di design-system.css.
 * I primitivi generici (PageContainer, Card, Input, Alert, Spinner...) vivono in components/ui.
 */
import styled, { css } from 'styled-components';
import { Card, Label } from '../ui';

export type RilevazioneTone = 'primary' | 'success' | 'error';

const toneBg: Record<RilevazioneTone, string> = {
  primary: 'var(--primary-700)',
  success: 'var(--success)',
  error: 'var(--error)'
};

/** Card di sezione con padding ampio e margine inferiore, basata sul primitivo Card condiviso. */
export const SectionCard = styled(Card)`
  padding: var(--space-xl);
  margin-bottom: var(--space-xl);
  overflow: visible;

  @media (max-width: 768px) {
    padding: var(--space-md);
    margin-bottom: var(--space-lg);
  }
`;

/** Griglia data / punto vendita in testa al form. */
export const FormSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-xl);
  margin-bottom: var(--space-xl);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-md);
    margin-bottom: var(--space-md);
  }
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

/** Label di campo con icona lucide affiancata. */
export const FieldLabel = styled(Label)`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);

  svg {
    flex-shrink: 0;
    color: var(--primary-700);
  }
`;

/** Griglia delle card gusto. Collassa a 2 colonne, poi 1, su tablet/telefono. */
export const GustiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--space-md);

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

/**
 * Card di un gusto. Lo stato selezionato si attiva con className="selected";
 * il tono ($tone) decide il colore di evidenziazione (primary di default, error per lo smaltimento).
 */
export const GustoCard = styled.div<{ $tone?: RilevazioneTone }>`
  background: var(--gray-50);
  padding: var(--space-lg);
  border-radius: var(--border-radius-xl);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &.selected {
    box-shadow: var(--shadow-md);
    ${({ $tone = 'primary' }) =>
      $tone === 'error'
        ? css`
            border-color: var(--error);
            background: var(--error-soft);
          `
        : css`
            border-color: var(--primary-500);
            background: var(--primary-50);
          `}
  }

  @media (max-width: 768px) {
    padding: var(--space-md);
  }
`;

export const GustoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
  gap: var(--space-sm);
`;

export const GustoName = styled.div`
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

/** Pallino colore del gusto (il colore arriva dai dati, non dai token). */
export const GustoColor = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid var(--card-bg);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
`;

/** Pannello riepilogo stock dentro la card gusto (giacenza). */
export const StockInfo = styled.div`
  background: var(--info-soft);
  border-radius: var(--border-radius-lg);
  padding: var(--space-sm) var(--space-md);
  margin-bottom: var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
`;

export const StockRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.3rem;

  &:last-child {
    margin-bottom: 0;
    font-weight: var(--font-weight-bold);
    padding-top: 0.3rem;
    border-top: 1px solid var(--gray-200);
  }
`;

/** Badge di stock disponibile (smaltimento). */
export const StockBadge = styled.div`
  background: var(--success);
  color: white;
  padding: 0.3rem 0.7rem;
  border-radius: 20px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
`;

/** Riga dei controlli quantità in fondo alla card gusto. */
export const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-top: auto;
`;

/** Bottone rotondo +/-; il tono definisce il colore semantico (error = togli, success = aggiungi). */
export const QuantityButton = styled.button<{ $tone?: RilevazioneTone }>`
  background: ${({ $tone = 'primary' }: { $tone?: RilevazioneTone }) =>
    $tone === 'primary' ? 'var(--primary-600)' : toneBg[$tone]};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    filter: brightness(0.9);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/** Variante larga a pillola (es. "Finito"). */
export const QuantityButtonWide = styled.button`
  background: var(--gray-500);
  color: white;
  border: none;
  height: 40px;
  padding: 0 12px;
  border-radius: 20px;
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--gray-600);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuantityInput = styled.input`
  flex: 1;
  min-width: 64px;
  width: 72px;
  padding: 0.5rem 0.25rem;
  border: 2px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  text-align: center;
  font-weight: var(--font-weight-bold);
  background: var(--input-bg);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary-500);
  }
`;

/** Card riepilogo totale (kg) a fondo pagina. */
export const SummaryCard = styled.div<{ $tone?: RilevazioneTone }>`
  background: ${({ $tone = 'primary' }: { $tone?: RilevazioneTone }) => toneBg[$tone]};
  color: white;
  padding: var(--space-xl);
  border-radius: var(--border-radius-xl);
  text-align: center;
  margin-bottom: var(--space-xl);

  @media (max-width: 768px) {
    padding: var(--space-lg);
  }
`;

export const SummaryTitle = styled.h3`
  font-size: 1.2rem;
  margin: 0 0 var(--space-sm);
`;

export const SummaryValue = styled.div`
  font-size: 3rem;
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-sm);

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

export const SummaryUnit = styled.span`
  font-size: 1.5rem;
  opacity: 0.9;
`;

/** Bottone di invio principale della pagina. */
export const SubmitButton = styled.button<{ $tone?: RilevazioneTone }>`
  background: ${({ $tone = 'primary' }: { $tone?: RilevazioneTone }) => toneBg[$tone]};
  color: white;
  border: none;
  padding: var(--space-md) var(--space-xl);
  border-radius: 10px;
  font-size: 1.1rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  transition: all 0.2s;

  &:hover:not(:disabled) {
    ${({ $tone = 'primary' }) =>
      $tone === 'primary'
        ? css`
            background: var(--primary-800);
          `
        : css`
            filter: brightness(0.9);
            transform: translateY(-2px);
          `}
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/** Contenitore che centra il bottone di invio. */
export const CenteredActions = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-xl);
`;

/** Banner informativo in testa alla pagina. */
export const InfoBanner = styled.div`
  background: var(--info-soft);
  color: var(--info);
  border: 1px solid var(--info);
  padding: var(--space-lg);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--space-xl);
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);

  svg {
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: var(--space-md);
    margin-bottom: var(--space-lg);
  }
`;

/** Banner di avvertenza (smaltimento). */
export const WarningBanner = styled(InfoBanner)`
  background: var(--warning-soft);
  color: var(--warning);
  border-color: var(--warning);
`;

/** Stato vuoto centrato dentro una card. */
export const EmptyState = styled.div`
  padding: var(--space-2xl);
  text-align: center;
  color: var(--text-muted);
  font-size: 1.1rem;
`;
